import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { BOXES } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const BoxForm = NiceModal.create(({ id, modalName, setBoxValues }) => {
	const modal = useModal();
	const [isMinimizing, setIsMinimizing] = useState(false);

	const { closeModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();
	const isEditMode = !!id;

	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					length: yup.number().min(0).max(9999).positive().nullable().required(),
					width: yup.number().min(0).max(9999).positive().nullable().required(),
					height: yup.number().min(0).max(9999).positive().nullable().required(),
					default: yup.boolean().nullable()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			length: null,
			width: null,
			height: null,
			default: false
		}
	});

	const {
		handleSubmit,
		watch,
		reset,
		control,
		setError,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, dirtyFields }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && Object.keys(dirtyFields).length > 0 && !isMinimizing);

	const watchedFields = useWatch({ control });

	useEffect(() => {
		const { name, length, width, height } = watchedFields;
		if (!name && !length && !width && !height && isDirty) {
			reset({}, { keepValues: true });
		}
	}, [watchedFields]);

	const fillBoxFormInEdit = () => {
		setIsLoading(true);
		axiosPrivate
			.post(BOXES.GET_BOX, {
				ID: Number(id)
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						name: data.name ?? null,
						length: data.length ?? null,
						width: data.width ?? null,
						height: data.height ?? null,
						default: data.defaultBox === 'yes'
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => AppLogger.error(error))
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (id) {
			fillBoxFormInEdit();
		}
	}, [id]);

	// Helpers
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = async (data) => {
		setIsLoading(true);
		const URL = id ? BOXES.UPDATE_BOX : BOXES.ADD_BOX;
		try {
			const { data: response, status } = await axiosPrivate.post(URL, {
				ID: id,
				name: data.name,
				length: data.length,
				width: data.width,
				height: data.height,
				default: data.default
			});

			if (status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.BOX_LIST));
				await axiosPrivate.post(BOXES.GET_DEFAULT_BOX).then((response) => {
					const newDefaultBox = response.data;
					dispatch(setUserDetails({ defaultBox: newDefaultBox ? newDefaultBox : undefined }));
				});
				setBoxValues && setBoxValues(response);
				handleCloseModal();
			}
		} catch (error) {
			AppLogger.error(error);
			if (error.response?.status === 406) {
				showError({
					header: 'error.F010',
					message: 'errorMsg.boxName'
				});
				setError('name', { type: 'custom', message: 'errorMsg.boxName' });
				return;
			}
			showError();
			handleCloseModal();
		} finally {
			setIsLoading(false);
		}
	};

	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal
			fullwidth
			maxWidth="small"
			name={modalName}
			className="modal-box"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={isEditMode ? 'editBox' : 'addNewBox'}
					tooltip={isEditMode ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{isEditMode ? 'editBox' : 'addNewBox'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{/* boxName */}
					<FormItem colSpan={defaultColSpan} label="boxName" isLoading={isLoading} required>
						<StringInput name="name" />
					</FormItem>
					{/* boxName */}
					{/* length */}
					<FormItem colSpan={defaultColSpan} label="length" isLoading={isLoading} required>
						<IntegerInput name="length" min={0} unit="cm" />
					</FormItem>
					{/* length */}
					{/* width */}
					<FormItem colSpan={defaultColSpan} label="width" isLoading={isLoading} required>
						<IntegerInput name="width" min={0} unit="cm" />
					</FormItem>
					{/* width */}
					{/* height */}
					<FormItem colSpan={defaultColSpan} label="height" isLoading={isLoading} required>
						<IntegerInput name="height" min={0} unit="cm" />
					</FormItem>
					{/* height */}
					{/* default */}
					<FormItem
						colSpan={defaultColSpan}
						label="defaultBox"
						direction="row-reverse"
						isLoading={isLoading}
						style={{ marginTop: '2.5rem' }}
						childStyle={{ marginBottom: '0' }}>
						<SwitchInput name="default" />
					</FormItem>
					{/* default */}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					disabled={!isDirty || Object.keys(dirtyFields).length === 0}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label={isEditMode ? 'save' : 'button.add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default BoxForm;
